/**
 * SecureStorage Utility
 * 
 * Provides secure localStorage operations with validation, sanitization, and error handling.
 * Implements security measures to prevent XSS, injection attacks, and data corruption.
 */

interface StorageValidationRules {
  maxStringLength?: number;
  maxArrayLength?: number;
  maxObjectKeys?: number;
  allowedTypes?: ('string' | 'number' | 'boolean' | 'object' | 'array')[];
}

interface StorageSchema {
  [key: string]: StorageValidationRules;
}

// Security constants
const MAX_STORAGE_SIZE = 50 * 1024; // 50KB per key
const DEFAULT_MAX_STRING_LENGTH = 5000;
const DEFAULT_MAX_ARRAY_LENGTH = 100;
const DEFAULT_MAX_OBJECT_KEYS = 50;

// Dangerous patterns to detect and sanitize
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /vbscript:/gi,
  /on\w+\s*=/gi,
  /data:text\/html/gi,
  /<iframe/gi,
  /<embed/gi,
  /<object/gi,
  /expression\s*\(/gi,
  /url\s*\(/gi
];

// Schema definitions for known storage keys
const STORAGE_SCHEMAS: StorageSchema = {
  'compliance-filters': {
    maxObjectKeys: 10,
    allowedTypes: ['object']
  },
  'dashboard-filters': {
    maxObjectKeys: 15,
    allowedTypes: ['object']
  },
  'navigation-state': {
    maxObjectKeys: 20,
    allowedTypes: ['object']
  },
  'sidebar-state': {
    maxObjectKeys: 5,
    allowedTypes: ['object']
  },
  'resizable-panel-width': {
    allowedTypes: ['number']
  }
};

class SecureStorageError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'SecureStorageError';
  }
}

export class SecureStorage {
  /**
   * Safely get an item from localStorage with validation and fallback
   */
  static get<T>(key: string, fallback: T): T {
    try {
      // Validate key
      this.validateKey(key);
      
      const item = localStorage.getItem(key);
      if (item === null) {
        return fallback;
      }

      // Parse JSON safely
      const parsed = this.safeParseJSON(item);
      if (parsed === null) {
        console.warn(`SecureStorage: Invalid JSON for key "${key}", using fallback`);
        return fallback;
      }

      // Validate against schema if available
      const schema = STORAGE_SCHEMAS[key];
      if (schema && !this.validateData(parsed, schema)) {
        console.warn(`SecureStorage: Schema validation failed for key "${key}", using fallback`);
        return fallback;
      }

      // Sanitize the data
      const sanitized = this.sanitizeData(parsed);
      
      return sanitized as T;
    } catch (error) {
      console.error(`SecureStorage.get error for key "${key}":`, error);
      return fallback;
    }
  }

  /**
   * Safely set an item in localStorage with validation and sanitization
   */
  static set<T>(key: string, value: T): void {
    try {
      // Validate key
      this.validateKey(key);

      // Validate value
      if (value === undefined) {
        throw new SecureStorageError('Cannot store undefined value', 'INVALID_VALUE');
      }

      // Sanitize the data
      const sanitized = this.sanitizeData(value);

      // Validate against schema if available
      const schema = STORAGE_SCHEMAS[key];
      if (schema && !this.validateData(sanitized, schema)) {
        throw new SecureStorageError(`Data validation failed for key "${key}"`, 'VALIDATION_FAILED');
      }

      // Serialize safely
      const serialized = JSON.stringify(sanitized);

      // Check size limits
      if (serialized.length > MAX_STORAGE_SIZE) {
        throw new SecureStorageError(`Data too large for key "${key}" (${serialized.length} > ${MAX_STORAGE_SIZE})`, 'SIZE_LIMIT_EXCEEDED');
      }

      // Store the data
      localStorage.setItem(key, serialized);
    } catch (error) {
      if (error instanceof SecureStorageError) {
        throw error;
      }
      throw new SecureStorageError(`Failed to store data for key "${key}": ${error}`, 'STORAGE_FAILED');
    }
  }

  /**
   * Remove an item from localStorage
   */
  static remove(key: string): void {
    try {
      this.validateKey(key);
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`SecureStorage.remove error for key "${key}":`, error);
    }
  }

  /**
   * Clear all items from localStorage (use with caution)
   */
  static clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('SecureStorage.clear error:', error);
    }
  }

  /**
   * Check if localStorage is available
   */
  static isAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate storage key
   */
  private static validateKey(key: string): void {
    if (!key || typeof key !== 'string') {
      throw new SecureStorageError('Key must be a non-empty string', 'INVALID_KEY');
    }

    if (key.length > 100) {
      throw new SecureStorageError('Key too long (max 100 characters)', 'KEY_TOO_LONG');
    }

    // Check for dangerous characters
    if (/[<>'"&\x00-\x1f\x7f-\x9f]/.test(key)) {
      throw new SecureStorageError('Key contains invalid characters', 'INVALID_KEY_CHARS');
    }
  }

  /**
   * Safely parse JSON with error handling
   */
  private static safeParseJSON(json: string): any {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  /**
   * Validate data against schema rules
   */
  private static validateData(data: any, schema: StorageValidationRules): boolean {
    try {
      // Check allowed types
      if (schema.allowedTypes) {
        const dataType = Array.isArray(data) ? 'array' : typeof data;
        if (!schema.allowedTypes.includes(dataType as any)) {
          return false;
        }
      }

      // Validate strings
      if (typeof data === 'string') {
        const maxLength = schema.maxStringLength || DEFAULT_MAX_STRING_LENGTH;
        if (data.length > maxLength) {
          return false;
        }
      }

      // Validate arrays
      if (Array.isArray(data)) {
        const maxLength = schema.maxArrayLength || DEFAULT_MAX_ARRAY_LENGTH;
        if (data.length > maxLength) {
          return false;
        }
      }

      // Validate objects
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const maxKeys = schema.maxObjectKeys || DEFAULT_MAX_OBJECT_KEYS;
        if (Object.keys(data).length > maxKeys) {
          return false;
        }
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Sanitize data to prevent XSS and injection attacks
   */
  private static sanitizeData(data: any): any {
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }

    if (data && typeof data === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(data)) {
        const sanitizedKey = this.sanitizeString(key);
        sanitized[sanitizedKey] = this.sanitizeData(value);
      }
      return sanitized;
    }

    return data;
  }

  /**
   * Sanitize string content
   */
  private static sanitizeString(str: string): string {
    if (typeof str !== 'string') return str;

    let sanitized = str;

    // Remove dangerous patterns
    DANGEROUS_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    // Limit length
    if (sanitized.length > DEFAULT_MAX_STRING_LENGTH) {
      sanitized = sanitized.substring(0, DEFAULT_MAX_STRING_LENGTH);
    }

    return sanitized;
  }

  /**
   * Get storage usage information
   */
  static getStorageInfo(): { used: number; available: number; keys: string[] } {
    const keys = Object.keys(localStorage);
    let used = 0;

    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        used += key.length + value.length;
      }
    });

    // Estimate available space (browsers typically allow 5-10MB)
    const estimated = 5 * 1024 * 1024; // 5MB estimate
    const available = Math.max(0, estimated - used);

    return { used, available, keys };
  }
}

// Export for convenience
export default SecureStorage;